/**
 * Alley geometry queries. The robot's collision footprint is a TWO-CIRCLE
 * model (front + rear circle of radius width/2, offset along the body axis)
 * so collision geometry accurately matches the visible 0.6 x 0.9 m box —
 * the old single circumscribed circle made the robot behave 1.08 m wide.
 */

import { clamp } from "../core/vec";
import {
  KERB_HEIGHT_M,
  SAFETY_LOOKAHEAD,
  WHEEL_HALF_BASE_M,
  WHEEL_HALF_TRACK_M,
  WHEEL_RADIUS_M,
} from "./constants";
import type { Obstacle, RobotState, SafetyHazard, WorldDef } from "./types";

export interface RobotShape {
  /** Radius of each body circle = width/2. */
  r: number;
  /** Axial offset of the circle centers from the body center. */
  d: number;
  /** Enclosing radius (r + d), used for conservative TTC checks. */
  effR: number;
}

export function robotShape(profile: { width_m: number; length_m: number }): RobotShape {
  const r = profile.width_m / 2;
  const d = Math.max(0, (profile.length_m - profile.width_m) / 2);
  return { r, d, effR: r + d };
}

/** The two body-circle centers for a robot pose. */
export function bodyCircles(
  robot: { x: number; z: number; heading: number },
  shape: RobotShape,
): Array<{ x: number; z: number }> {
  const sx = Math.sin(robot.heading) * shape.d;
  const sz = Math.cos(robot.heading) * shape.d;
  return [
    { x: robot.x + sx, z: robot.z + sz },
    { x: robot.x - sx, z: robot.z - sz },
  ];
}

/** Lateral (x) extent of the body from its center for a given heading. */
export function lateralExtent(shape: RobotShape, heading: number): number {
  return shape.r + shape.d * Math.abs(Math.sin(heading));
}

/**
 * Free distance the robot could translate along `rayHeading` (with its body at
 * `bodyHeading`) before touching a curb/wall, an obstacle, or a dynamic hazard
 * (at its current position). Minimum over both body circles; up to
 * SAFETY_LOOKAHEAD.
 */
export function freeDistance(
  world: WorldDef,
  robot: { x: number; z: number },
  bodyHeading: number,
  rayHeading: number,
  shape: RobotShape,
  hazards: readonly SafetyHazard[],
  /** Skip curbs/walls (the wall clamp slides along them); obstacles + hazards only. */
  skipCurbs = false,
): number {
  const dx = Math.sin(rayHeading);
  const dz = Math.cos(rayHeading);
  let best = SAFETY_LOOKAHEAD;

  for (const c of bodyCircles({ ...robot, heading: bodyHeading }, shape)) {
    if (skipCurbs) {
      // no curb term
    } else if (world.network) {
      // Curbs are the road network's signed-distance boundary (ADR-022).
      best = Math.min(best, world.network.freeAlong(c.x, c.z, dx, dz, shape.r, SAFETY_LOOKAHEAD));
    } else {
      // Straight walls inflated by the circle radius: |x| = alleyHalfWidth - r.
      const wallX = world.alleyHalfWidth - shape.r;
      if (dx > 1e-9) {
        const t = (wallX - c.x) / dx;
        best = Math.min(best, Math.max(0, t));
      } else if (dx < -1e-9) {
        const t = (-wallX - c.x) / dx;
        best = Math.min(best, Math.max(0, t));
      }
    }

    for (const ob of world.obstacles) {
      // Neither a thing driven over nor a hole shortens the free distance: a
      // pothole registering as a wall two metres ahead would have the safety
      // model report a time-to-collision against a hole in the road, and would
      // brake for the very thing the robot is supposed to fail to notice.
      if (rideable(world, ob) || isUnseenPit(world, ob)) continue;
      const t = rayVsAabb(c.x, c.z, dx, dz, ob, shape.r);
      if (t !== null) best = Math.min(best, t);
    }

    for (const hz of hazards) {
      const t = rayVsCircle(c.x, c.z, dx, dz, hz.x, hz.z, shape.r + hz.radius);
      if (t !== null) best = Math.min(best, t);
    }
  }

  return Math.max(0, best);
}

/** Slab-method ray vs AABB inflated by `r`. Returns entry distance or null. */
function rayVsAabb(
  x: number,
  z: number,
  dx: number,
  dz: number,
  ob: Obstacle,
  r: number,
): number | null {
  const minX = ob.x - ob.halfW - r;
  const maxX = ob.x + ob.halfW + r;
  const minZ = ob.z - ob.halfD - r;
  const maxZ = ob.z + ob.halfD + r;

  let tMin = 0;
  let tMax = SAFETY_LOOKAHEAD;

  for (const [p, d, lo, hi] of [
    [x, dx, minX, maxX],
    [z, dz, minZ, maxZ],
  ] as const) {
    if (Math.abs(d) < 1e-9) {
      if (p < lo || p > hi) return null;
    } else {
      let t1 = (lo - p) / d;
      let t2 = (hi - p) / d;
      if (t1 > t2) [t1, t2] = [t2, t1];
      tMin = Math.max(tMin, t1);
      tMax = Math.min(tMax, t2);
      if (tMin > tMax) return null;
    }
  }
  return tMin;
}

/** Ray vs circle of radius R at (cx, cz). Returns entry distance or null. */
function rayVsCircle(
  x: number,
  z: number,
  dx: number,
  dz: number,
  cx: number,
  cz: number,
  R: number,
): number | null {
  const ox = x - cx;
  const oz = z - cz;
  const b = ox * dx + oz * dz;
  const c = ox * ox + oz * oz - R * R;
  if (c <= 0) return 0; // already inside
  const disc = b * b - c;
  if (disc < 0) return null;
  const t = -b - Math.sqrt(disc);
  return t >= 0 ? t : null;
}

/** Distance from a point to an AABB (0 when inside). */
function distToAabb(px: number, pz: number, ob: Obstacle): number {
  const dx = Math.max(Math.abs(px - ob.x) - ob.halfW, 0);
  const dz = Math.max(Math.abs(pz - ob.z) - ob.halfD, 0);
  return Math.hypot(dx, dz);
}

/**
 * Height of the ground under a point, in metres above the carriageway
 * (ADR-058).
 *
 * The sim has always been flat: a kerb was a line you could or could not
 * cross, a speed bump was a one-off subtraction from `speed`. Nothing rose,
 * nothing tilted, so a robot mounting a pavement slid onto it at road level
 * and a Gờ giảm tốc was an invisible tax. This gives the world a surface,
 * which the chassis model then rides.
 */
export function surfaceHeightAt(world: WorldDef, x: number, z: number): number {
  let h = 0;
  // Up on the footway, if there is a kerb line to be past.
  const net = world.network;
  if (net && net.sdf(x, z) > 0) h = KERB_HEIGHT_M;
  // Speed bumps: a rounded hump across the lane, not a step. Squared falloff
  // so the wheel climbs and drops instead of teleporting to the crest.
  for (const ob of world.obstacles) {
    if (!ob.bump) continue;
    const dx = Math.abs(x - ob.x);
    const dz = Math.abs(z - ob.z);
    if (dx > ob.halfW || dz > ob.halfD) continue;
    const across = ob.halfW >= ob.halfD ? dz / Math.max(ob.halfD, 1e-6) : dx / Math.max(ob.halfW, 1e-6);
    h = Math.max(h, ob.height * (1 - across * across));
  }
  return h;
}

/**
 * Is this obstacle low enough that the robot crosses it instead of hitting it?
 *
 * Height, not a list of kinds: the map format lets a map invent scenery, and
 * a rule written against names would silently treat anything new as solid.
 */
export function rideable(world: WorldDef, ob: Obstacle): boolean {
  // A hole is never "ridden over". It is low, so the height test alone said
  // yes, and the same pothole then reported a rough_ride on the way in and a
  // fall_in an instant later — one object, two contradictory verdicts, and a
  // prompt that said the robot bumped over the thing it fell into.
  if (ob.pit) return false;
  // A bump is rideable in EVERY mode, assisted included. It was decor before
  // this flag existed precisely because a solid bump is a wall; making it a
  // prop only works if no mode ever treats it as one (ADR-045).
  if (ob.bump) return true;
  return ob.height <= (world.rideOverBelow ?? 0);
}

/**
 * The kind of the low obstacle the robot's body is currently over, if any.
 *
 * Separate from `checkCollision` because these are not collisions — nothing
 * stops, nothing is scored against the driver as a crash — but they are not
 * nothing either: crossing one costs speed and belongs in the run report.
 */
export function rideOverKind(
  world: WorldDef,
  robot: RobotState,
  shape: RobotShape,
): string | null {
  if (!(world.rideOverBelow ?? 0)) return null;
  for (const c of bodyCircles(robot, shape)) {
    for (const ob of world.obstacles) {
      if (!rideable(world, ob)) continue;
      if (distToAabb(c.x, c.z, ob) < shape.r) return ob.kind ?? "obstacle";
    }
  }
  return null;
}

/** A hole, under the model where holes are not detected (ADR-043). */
function isUnseenPit(world: WorldDef, ob: Obstacle): boolean {
  return ob.pit === true && (world.rideOverBelow ?? 0) > 0;
}

/** A wheel that has gone into a hole, and which way the robot goes with it. */
export interface PitEntry {
  /** The hole's kind, for the report and the warning. */
  kind: string;
  /** How many of the four wheels are over it. */
  wheels: number;
  /** "left" or "right" if only one side dropped, else null (a straight drop). */
  roll: "left" | "right" | null;
}

/**
 * Has a wheel gone into a hole?
 *
 * A wheel bridges a gap only up to about its own radius; past that the axle
 * drops below the lip and the wheel is in. So the test is the hole's NARROWEST
 * span against the wheel radius — not its depth, and not the robot's body,
 * which is what the ride-over model wrongly used. A pothole 0.9 m across is
 * six times what a 142 mm wheel can bridge.
 *
 * The four ground contacts are checked separately because that is what decides
 * whether the robot drops level or goes over: one side in and the other still
 * on the road is a rollover, and a rollover is the thing worth recording.
 */
export function pitEntered(
  world: WorldDef,
  robot: RobotState,
  shape: RobotShape,
): PitEntry | null {
  void shape;
  const sin = Math.sin(robot.heading);
  const cos = Math.cos(robot.heading);
  const contacts = [
    { lat: -WHEEL_HALF_TRACK_M, lon: WHEEL_HALF_BASE_M, side: "left" as const },
    { lat: WHEEL_HALF_TRACK_M, lon: WHEEL_HALF_BASE_M, side: "right" as const },
    { lat: -WHEEL_HALF_TRACK_M, lon: -WHEEL_HALF_BASE_M, side: "left" as const },
    { lat: WHEEL_HALF_TRACK_M, lon: -WHEEL_HALF_BASE_M, side: "right" as const },
  ];
  for (const ob of world.obstacles) {
    if (!ob.pit) continue;
    // Narrowest span across the hole. A long crack the wheel can straddle
    // lengthways is still a trap if it is wide.
    const span = 2 * Math.min(ob.halfW, ob.halfD);
    if (span <= WHEEL_RADIUS_M) continue; // the wheel rolls straight over it
    let left = 0;
    let right = 0;
    for (const c of contacts) {
      const x = robot.x + c.lat * cos + c.lon * sin;
      const z = robot.z - c.lat * sin + c.lon * cos;
      if (Math.abs(x - ob.x) <= ob.halfW && Math.abs(z - ob.z) <= ob.halfD) {
        if (c.side === "left") left++;
        else right++;
      }
    }
    const wheels = left + right;
    if (wheels === 0) continue;
    const roll = left > 0 && right === 0 ? "left" : right > 0 && left === 0 ? "right" : null;
    return { kind: ob.kind ?? "hole", wheels, roll };
  }
  return null;
}

export interface CollisionResult {
  collided: boolean;
  /** "obstacle", or the hazard's id ("pedestrian" for the lab levels). */
  with: string | null;
  /** Internal warning detail; telemetry still identifies a static obstacle. */
  obstacleKind?: string;
}

/** Hard-overlap test: hazards and obstacles collide; walls/curbs only clamp. */
export function checkCollision(
  world: WorldDef,
  robot: RobotState,
  shape: RobotShape,
  hazards: readonly SafetyHazard[],
): CollisionResult {
  for (const c of bodyCircles(robot, shape)) {
    for (const hz of hazards) {
      if (Math.hypot(c.x - hz.x, c.z - hz.z) < shape.r + hz.radius) {
        return { collided: true, with: hz.id };
      }
    }
    for (const ob of world.obstacles) {
      if (rideable(world, ob) || isUnseenPit(world, ob)) continue;
      if (distToAabb(c.x, c.z, ob) < shape.r) {
        return ob.kind === "tree"
          ? { collided: true, with: "obstacle", obstacleKind: "tree" }
          : { collided: true, with: "obstacle" };
      }
    }
  }
  return { collided: false, with: null };
}

/** Keep the robot's body inside the walls/curbs (last-resort clamp, no fail). */
export function clampToWalls(world: WorldDef, robot: RobotState, shape: RobotShape): void {
  if (world.network) {
    // Push each body circle back inside the road along the curb normal and
    // move the body by the largest correction: the robot slides along curbs
    // of any shape (junction corners, the roundabout island) and never
    // wedges into them.
    let cx = 0;
    let cz = 0;
    let worst = 0;
    for (const c of bodyCircles(robot, shape)) {
      const p = world.network.pushInside(c.x, c.z, shape.r);
      const dx = p.x - c.x;
      const dz = p.z - c.z;
      const mag = Math.hypot(dx, dz);
      if (mag > worst) {
        worst = mag;
        cx = dx;
        cz = dz;
      }
    }
    if (worst > 0) {
      robot.x += cx;
      robot.z += cz;
    }
    return;
  }
  const extent = lateralExtent(shape, robot.heading);
  const lim = world.alleyHalfWidth - extent;
  robot.x = clamp(robot.x, -lim, lim);
}

/**
 * Keep the robot on the road OR on the pavement beside it, but no further.
 *
 * The plain clamp treats a kerb as a wall the robot slides along, so a machine
 * that in life would bump up onto the pavement instead skates past it. Under
 * the real-physics model a kerb is something you CAN get over — that is what
 * the kerb-climb metric has been counting all along — and this lets it happen
 * while still stopping the robot from driving through shopfronts.
 *
 * `reach` is how far past the kerb line the world is still solid ground.
 * Beyond it the ordinary clamp applies, so there is always a boundary.
 */
export function clampToPavement(
  world: WorldDef,
  robot: RobotState,
  shape: RobotShape,
  reach: number,
): void {
  const net = world.network;
  if (!net) {
    clampToWalls(world, robot, shape);
    return;
  }
  let worstOut = -Infinity;
  for (const c of bodyCircles(robot, shape)) {
    worstOut = Math.max(worstOut, net.sdf(c.x, c.z) + shape.r);
  }
  if (worstOut <= reach) return; // on the road, or on the pavement beside it
  // Past the pavement: put it back to the pavement's edge rather than all the
  // way to the road, or a robot that mounted the kerb would be yanked off it.
  const before = { x: robot.x, z: robot.z };
  clampToWalls(world, robot, shape);
  const dx = robot.x - before.x;
  const dz = robot.z - before.z;
  const mag = Math.hypot(dx, dz);
  if (mag > 1e-9) {
    const keep = Math.max(0, mag - reach) / mag;
    robot.x = before.x + dx * keep;
    robot.z = before.z + dz * keep;
  }
}

/** Clearance from the robot center to the nearest curb/wall beyond its body. */
export function wallClearance(world: WorldDef, robot: RobotState, shape: RobotShape): number {
  if (world.network) {
    let best = Infinity;
    for (const c of bodyCircles(robot, shape)) {
      best = Math.min(best, world.network.clearance(c.x, c.z) - shape.r);
    }
    return best;
  }
  const extent = lateralExtent(shape, robot.heading);
  return world.alleyHalfWidth - extent - Math.abs(robot.x);
}

/**
 * Destination test. Lab levels: inside the marker disc or across the
 * destination line. Mission: inside the disc, or path progress past the
 * customer's line while within the corridor (frame supplied by the sim).
 */
export function atDestination(
  world: WorldDef,
  robot: RobotState,
  frame?: { s: number; d: number; halfWidth: number } | null,
): boolean {
  const d = world.destination;
  if (Math.hypot(robot.x - d.x, robot.z - d.z) <= world.destinationRadius) return true;
  if (world.path && frame) {
    return frame.s >= world.path.length - 0.5 && Math.abs(frame.d) <= frame.halfWidth;
  }
  return robot.z >= d.z && Math.abs(robot.x - d.x) <= world.destinationLineHalfWidth;
}
